import { Button, Form, Select, Space, TableProps } from "antd";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppSelector } from "../../../../app/store";
import AddIcon from "../../../../assets/icons/AddIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import CustomTable from "../../../../components/ui/CustomTable";
import {
  useAssignUserWalletRoleMutation,
  useGetUsersWithDisbursementAccessQuery,
  useGetUserWalletRoleByIdQuery,
} from "../../../../features/portal/companyOnboard/companyOnboardApi";
import useError from "../../../../hooks/useError";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { toast } from "../../../../lib/toast";
import { countParPageSerialNumber } from "../../../../utils/number";
import { capitalize } from "../../../../utils/string";
import { addKeyInArray } from "../../../../utils/utils";

export default function UserRoleAssignList() {
  const backLink = "/admin/sheba-pay-wallet";
  const [form] = Form.useForm();
  const { id } = useParams();
  const [formType, setFormType] = useState<"role" | null>(null);

  const { page, limit, handlePagination, handleLimit, filter, handleFilter } =
    useTable<{ role?: string; page?: number; limit?: number }>();

  const { user } = useAppSelector((state) => state.auth);

  const {
    data: getUserWalletRoleByIdData,
    isError: getUserWalletRoleByIdIsError,
    error: getUserWalletRoleByIdError,
    isLoading: getUserWalletRoleByIdIsLoading,
  } = useGetUserWalletRoleByIdQuery(
    {
      id: Number(id),
      role: filter?.role,
      page,
      limit,
    },
    {
      skip: !id,
    }
  );

  const {
    data: getUsersWithDisbursementAccessData,
    isError: getUsersWithDisbursementAccessIsError,
    error: getUsersWithDisbursementAccessError,
  } = useGetUsersWithDisbursementAccessQuery(
    {
      params: {
        companyId: user?.data?.company_id,
      },
    },
    { skip: !user?.data?.company_id }
  );

  const [
    assignUserWalletRole,
    {
      isError: assignUserWalletRoleIsError,
      error: assignUserWalletRoleError,
      isLoading: assignUserWalletRoleIsLoading,
      isSuccess: assignUserWalletRoleIsSuccess,
    },
  ] = useAssignUserWalletRoleMutation();

  useError(
    getUserWalletRoleByIdIsError,
    getUserWalletRoleByIdError,
    "Get user wallet role by id fail"
  );

  useError(
    getUsersWithDisbursementAccessIsError,
    getUsersWithDisbursementAccessError,
    "Get users with disbursement access fail"
  );

  useError(
    assignUserWalletRoleIsError,
    assignUserWalletRoleError,
    "Assign user wallet role fail"
  );

  const onFinishRoleAssign = (values: any) => {
    const newData = {
      ...values,
      wallet_id: Number(id),
    };

    assignUserWalletRole(newData);
  };

  useEffect(() => {
    if (assignUserWalletRoleIsSuccess) {
      toast("success", "User role assigned successfully");
      form.resetFields();
      setFormType(null);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignUserWalletRoleIsSuccess]);

  const columns: TableProps<any>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },

    {
      title: "User Name",
      dataIndex: "user",
      render: (val) => (val ? val?.username : "N/A"),
    },

    {
      title: "Role Type",
      dataIndex: "role",
      align: "center",
      render: (val) => {
        return <span>{capitalize(val)}</span>;
      },
    },
  ];

  return (
    <>
      <SidebarLayout title="Add User Role" backLink={backLink}>
        {formType === "role" && (
          <div className="mb-6">
            <Form
              name="form_item_path"
              className="custom-form bg-white border border-extra-light border-solid rounded-md"
              layout="vertical"
              onFinish={onFinishRoleAssign}
              requiredMark="optional"
              size="large"
              form={form}
            >
              <div className="p-6">
                <div className="w-full mt-4 px-3 py-1.5 bg-[#f0f3f8] rounded-md justify-between items-center flex">
                  <div className="justify-start items-center gap-4 flex">
                    <div className="text-display-body text-lg font-semibold leading-[30px]">
                      Assign User Role
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-6 mt-4">
                  <Form.Item
                    label="Select User"
                    name="user_id"
                    rules={[{ required: true, message: "Please select user" }]}
                  >
                    <Select
                      placeholder="Select User"
                      className="w-full"
                      suffixIcon={<SelectDownIcon />}
                      style={{ width: "100%" }}
                      allowClear
                      options={
                        getUsersWithDisbursementAccessData?.data?.users?.map(
                          (item: any) => ({
                            label: item?.user?.username, // show username
                            value: item?.user?.id, // store user ID
                          })
                        ) || []
                      }
                    />
                  </Form.Item>

                  <Form.Item
                    label="Select Role"
                    name="role"
                    rules={[{ required: true, message: "Please select role" }]}
                  >
                    <Select
                      placeholder="Select Role"
                      className="w-full"
                      suffixIcon={<SelectDownIcon />}
                      style={{ width: "100%" }}
                      allowClear
                      options={[
                        { label: "Maker", value: "maker" },
                        { label: "Checker", value: "checker" },
                        { label: "Approver", value: "approver" },
                      ]}
                    />
                  </Form.Item>

                  {/* <div className="mt-6">
                    <Button
                      className="modal-button max-w-[160px] pt-6 pb-6"
                      type="primary"
                      htmlType="submit"
                      size="large"
                      // loading={isAssignRoleLoading}
                      // disabled={isSubmitDisabledRole}
                    >
                      Submit
                    </Button>
                  </div> */}
                </div>

                <Space className="w-full mt-2" size={12}>
                  <Button
                    className="normal-dark-button modal-button !h-[40px]"
                    size="middle"
                    onClick={() => setFormType(null)}
                  >
                    Cancel
                  </Button>

                  <Button
                    className="modal-button !h-[40px]"
                    type="primary"
                    htmlType="submit"
                    size="middle"
                    loading={assignUserWalletRoleIsLoading}
                  >
                    Submit
                  </Button>
                </Space>
              </div>
            </Form>
          </div>
        )}

        <CustomTable
          loading={getUserWalletRoleByIdIsLoading}
          limit={limit}
          page={page}
          columns={columns}
          total={getUserWalletRoleByIdData?.data?.totalLength}
          dataSource={addKeyInArray(
            getUserWalletRoleByIdData?.data?.userRoles || []
          )}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          filterSection={
            <>
              <Select
                placeholder="Select Role"
                className="!m-w-[70px]"
                suffixIcon={<SelectDownIcon />}
                allowClear
                options={[
                  { label: "Maker", value: "maker" },
                  { label: "Checker", value: "checker" },
                  { label: "Approver", value: "approver" },
                ]}
                value={filter?.role ?? undefined}
                onChange={(value) => handleFilter("role", value)}
              />

              <Button
                type="primary"
                className="middle-button"
                icon={<AddIcon />}
                onClick={() => setFormType("role")}
              >
                Role Assign
              </Button>
            </>
          }
        />
      </SidebarLayout>
    </>
  );
}
